import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_ENDPOINTS } from '../shared/constants/api.constants';
import { CurrentLocation, LocationSuggestion } from '../types/location.types';
import { LocationRepository, PlaceDetails } from './location-repository';

/**
 * Google-Places-backed LocationRepository. Every call goes through our own
 * backend proxy (/api/v2/location/...), which wraps the Google Maps/Places
 * REST APIs server-side - the browser never talks to Google directly and
 * never sees a Google API key. A future BackendLocationRepository (an
 * in-house geocoder) can implement the same interface and be swapped in via
 * the LOCATION_REPOSITORY provider with no UI changes.
 */
@Injectable({ providedIn: 'root' })
export class GooglePlacesService implements LocationRepository {

  constructor(private http: HttpClient) { }

  reverseGeocode(lat: number, lng: number, radius?: number): Observable<CurrentLocation | null> {
    const finalRadius = radius ?? 1;
    const url = `${API_ENDPOINTS.REVERSE_GEOCODE}?lat=${lat}&lng=${lng}&radius=${finalRadius}`;

    return this.http.get<any>(url).pipe(
      map(response => this.parseGeocodeResponse(response)),
      catchError(error => {
        console.error('Geocoding error:', error);
        return of(null);
      })
    );
  }

  getPincodeFromLatLng(lat: number, lng: number): Observable<string | null> {
    const url = `${API_ENDPOINTS.REVERSE_GEOCODE}?lat=${lat}&lng=${lng}`;

    return this.http.get<any>(url).pipe(
      map(res => {
        if (res.results && res.results.length) {
          for (const component of res.results[0].address_components) {
            if (component.types.includes('postal_code')) {
              return component.long_name;
            }
          }
        }
        return null;
      }),
      catchError(error => {
        console.error('Geocoding error:', error);
        return of(null);
      })
    );
  }

  searchPlaces(query: string): Observable<LocationSuggestion[]> {
    if (!query || query.length < 3) {
      return of([]);
    }

    const url = `${API_ENDPOINTS.PLACE_AUTOCOMPLETE}?input=${encodeURIComponent(query)}`;

    return this.http.get<any>(url).pipe(
      map(res => (res.predictions || []).map((p: any) => ({
        description: p.description,
        placeId: p.place_id,
        structured_formatting: p.structured_formatting
      }))),
      catchError(() => of([]))
    );
  }

  getPlaceDetails(placeId: string): Observable<PlaceDetails | null> {
    const url = `${API_ENDPOINTS.PLACE_DETAILS}?place_id=${placeId}`;

    return this.http.get<any>(url).pipe(
      map(response => {
        const location = response?.result?.geometry?.location;
        return location ? { lat: location.lat, lng: location.lng } : null;
      }),
      catchError(() => of(null))
    );
  }

  private parseGeocodeResponse(response: any): CurrentLocation | null {
    if (!response || !response.address || !response.latitude || !response.longitude) {
      return null;
    }

    const formattedAddress = response.address;
    const lat = response.latitude;
    const lng = response.longitude;

    let postalCode = '';
    let locality = '';
    let sector = '';
    let state = '';

    if (Array.isArray(response.components)) {
      response.components.forEach((component: any) => {
        if (component.types.includes('postal_code')) {
          postalCode = component.long_name;
        }
        if (component.types.includes('sublocality_level_1') || component.types.includes('sublocality')) {
          sector = component.long_name;
        }
        if (component.types.includes('locality')) {
          locality = component.long_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
          state = component.long_name;
        }
      });
    }

    if (!sector && locality) sector = locality;

    const addressRest = formattedAddress
      .replace(sector, '')
      .replace(/^,/, '')
      .trim();

    return {
      address: formattedAddress,
      sectorName: sector,
      addressRest: addressRest,
      latitude: lat,
      longitude: lng,
      pincode: postalCode,
      city: locality,
      state: state
    };
  }
}
